import type React from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'filled' | 'outline';
  color?:
    | 'default'
    | 'success'
    | 'warning'
    | 'danger'
    | 'info'
    | 'caution'
    | 'silver'
    | 'gold'
    | 'bronze';
}

export function Badge({
  className,
  variant = 'filled',
  color = 'default',
  children,
  ...props
}: BadgeProps) {
  // Base classes for all badges
  const baseClasses =
    'inline-flex items-center rounded-md px-[10px] py-[2px] text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border';

  // Variant-specific classes
  const outlineClasses =
    variant === 'outline' ? 'bg-transparent' : 'border-transparent';

  // Color classes based on variant and color
  let colorClasses = '';

  if (variant === 'filled') {
    switch (color) {
      case 'success':
        colorClasses = 'text-white bg-green-500 dark:bg-green-600 ';
        break;
      case 'warning':
        colorClasses = 'text-white bg-yellow-500 dark:bg-yellow-600 ';
        break;
      case 'danger':
        colorClasses = 'text-white bg-red-500 dark:bg-red-600 ';
        break;
      case 'info':
        colorClasses = 'text-white bg-blue-500 dark:bg-blue-600 ';
        break;
      case 'caution':
        colorClasses = 'text-white bg-yellow dark:bg-yellow ';
        break;
      case 'bronze':
        colorClasses = 'text-white bg-bronze text-white';
        break;
      case 'silver':
        colorClasses = 'text-white bg-silver text-white';
        break;
      case 'gold':
        colorClasses = 'bg-gold text-[rgb(50,50,50)]';
        break;
      case 'default':
      default:
        colorClasses = 'text-white bg-black dark:bg-gray-700';
        break;
    }
  } else {
    // Outline variant
    switch (color) {
      case 'success':
        colorClasses =
          'border-green-500 text-green-600 dark:border-green-400 dark:text-green-300';
        break;
      case 'warning':
        colorClasses =
          'border-yellow-500 text-yellow-600 dark:border-yellow-400 dark:text-yellow-300';
        break;
      case 'danger':
        colorClasses =
          'border-red text-red-600 dark:border-red-400 dark:text-red-300';
        break;
      case 'info':
        colorClasses =
          'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-300';
        break;
      case 'caution':
        colorClasses =
          'border-yellow text-yellow dark:border-yellow-400 dark:text-yellow-300';
        break;
      case 'silver':
        colorClasses = 'border-silver text-silver';
        break;
      case 'bronze':
        colorClasses = 'border-bronze text-bronze';
        break;
      case 'gold':
        colorClasses = 'border-gold text-gold';
        break;
      case 'default':
      default:
        colorClasses =
          'border-black text-primary dark:text-white dark:border-white';
        break;
    }
  }

  return (
    <div
      className={cn(baseClasses, outlineClasses, colorClasses, className)}
      {...props}
    >
      {children}
    </div>
  );
}
